import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Send } from "lucide-react";

export default function JarvisStrip() {
    return (
        <section
            className="relative overflow-hidden bg-mm-navy text-white"
            data-testid="jarvis-strip"
        >
            <div aria-hidden className="pointer-events-none absolute inset-0">
                <div className="absolute -top-40 right-10 h-[420px] w-[420px] rounded-full bg-mm-blue/25 blur-3xl" />
                <div className="absolute -bottom-40 -left-10 h-[360px] w-[360px] rounded-full bg-mm-sky/20 blur-3xl" />
            </div>

            <div className="mm-container relative py-20 sm:py-28">
                <div className="grid items-center gap-12 lg:grid-cols-2">
                    <div>
                        <div className="mm-eyebrow !text-mm-sky">Meet Jarvis</div>
                        <h2 className="mm-display mt-3 text-4xl sm:text-5xl">
                            A calm, patient concierge —{" "}
                            <span className="bg-gradient-to-r from-mm-sky to-white bg-clip-text text-transparent">
                                before you ever call.
                            </span>
                        </h2>
                        <p className="mt-5 max-w-xl text-white/80 leading-relaxed">
                            Jarvis is our AI concierge. Ask questions in plain language.
                            Learn what coverage makes sense for your situation. When
                            you're ready, a licensed Moving Mountains advisor takes it
                            from there.
                        </p>
                        <ul className="mt-6 space-y-2 text-sm text-white/75">
                            <li>• No sales pitch. No pressure. No data sold.</li>
                            <li>• Works on your phone, no download needed.</li>
                            <li>• Always hands off to a real, licensed human.</li>
                        </ul>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/jarvis" className="mm-btn-primary !bg-white !text-mm-navy hover:!bg-mm-sky hover:!text-mm-navy" data-testid="jarvis-strip-cta">
                                <Sparkles size={15} />
                                Start With Jarvis
                            </Link>
                            <Link
                                to="/how-it-works"
                                className="mm-btn-outline !border-white/50 !bg-transparent !text-white hover:!bg-white hover:!text-mm-navy"
                            >
                                How it works
                            </Link>
                        </div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="relative rounded-3xl border border-white/15 bg-white/[0.04] p-5 backdrop-blur"
                        data-testid="jarvis-mock"
                    >
                        <div className="mb-4 flex items-center gap-2 text-xs text-white/60">
                            <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-mm-sky" />
                            Jarvis · online
                        </div>

                        <div className="space-y-3 text-sm">
                            <Bubble from="assistant">
                                Welcome to Moving Mountains Inc. I'm Jarvis. May I have
                                your first name — and what brought you in today?
                            </Bubble>
                            <Bubble from="user">
                                Hi, I'm Elena. Looking at term life to protect the kids.
                            </Bubble>
                            <Bubble from="assistant">
                                Thank you, Elena. A few quick things and I'll connect
                                you with a licensed advisor for the details.
                            </Bubble>
                            <div className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2.5">
                                <div className="flex-1 text-xs text-white/50">
                                    Type your message…
                                </div>
                                <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-mm-navy">
                                    <Send size={14} />
                                </div>
                            </div>
                            <p className="pt-1 text-[0.68rem] text-white/50">
                                Static preview — the live experience launches soon.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function Bubble({ from = "assistant", children }) {
    const mine = from === "user";
    return (
        <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
            <div
                className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${
                    mine
                        ? "bg-mm-sky text-mm-navy"
                        : "bg-white/10 text-white"
                }`}
            >
                {children}
            </div>
        </div>
    );
}
